<template>
  <v-dialog v-model="dialog" width="640" :persistent="submitting">
    <v-card>
      <v-card-title class="text-h6">{{ $t('elabel_export_new') }}</v-card-title>

      <v-card-text class="mt-4 pb-0">
        <div v-if="!singleCampaign">
          <div class="field-label">{{ $t('campaigns') }}</div>
          <v-autocomplete
            class="scope-field"
            v-model="selectedCampaigns"
            :items="campaigns"
            :placeholder="$t('elabel_export_choose_campaigns')"
            item-text="name"
            item-value="id"
            data-test="campaigns-field"
            outlined
            multiple
            chips
            deletable-chips
            small-chips
            hide-details="auto"
          >
            <template v-if="allCampaignsSelected" v-slot:selection="{ index }">
              <v-chip v-if="index === 0" small close @click:close="selectedCampaigns = []">{{ $t('elabel_export_all_campaigns') }}</v-chip>
            </template>
            <template v-slot:prepend-item>
              <v-list-item @click="toggleAllCampaigns">
                <v-list-item-action>
                  <v-checkbox :input-value="allCampaignsSelected" hide-details dense />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('select_all') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-autocomplete>
        </div>

        <template v-if="singleCampaign">
          <div class="field-label">{{ $t('brands') }}</div>
          <v-autocomplete
            class="scope-field"
            v-model="selectedBrands"
            :items="brandItems"
            :loading="loadingBrands"
            :placeholder="$t('elabel_export_choose_brands')"
            item-text="name"
            item-value="id"
            data-test="brands-field"
            outlined
            multiple
            chips
            deletable-chips
            small-chips
            hide-details="auto"
          >
            <template v-if="allBrandsSelected" v-slot:selection="{ index }">
              <v-chip v-if="index === 0" small close @click:close="selectedBrands = []">{{ $t('brand_all_brands') }}</v-chip>
            </template>
            <template v-slot:prepend-item>
              <v-list-item @click="toggleAllBrands">
                <v-list-item-action>
                  <v-checkbox :input-value="allBrandsSelected" hide-details dense />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('select_all') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-autocomplete>
        </template>

        <div class="field-label mt-4">{{ $t('products') }}</div>
        <v-autocomplete
          class="scope-field"
          v-model="selectedProducts"
          :search-input.sync="productSearch"
          :items="productItems"
          :loading="loadingProducts"
          :disabled="productsLocked"
          :placeholder="$t(productsLocked ? lockedProductsPlaceholderKey : 'elabel_export_all_products')"
          :item-text="productLabel"
          :item-disabled="isProductDisabled"
          item-value="id"
          data-test="products-field"
          no-filter
          outlined
          multiple
          chips
          deletable-chips
          small-chips
          hide-details="auto"
          @blur="productSearch = ''"
        />
        <div v-if="productsAtLimit" class="caption grey--text text--darken-1 mt-1" data-test="products-limit">
          {{ $t('elabel_export_max_products', { count: productLimit }) }}
        </div>

        <p v-if="hasSelection" class="body-2 black--text mt-6 mb-0" data-test="selection-sentence">{{ sentence }}</p>
      </v-card-text>

      <v-card-actions class="pa-4 px-6 pb-6">
        <v-spacer></v-spacer>
        <v-btn
          text
          class="blue--text mr-4"
          :disabled="submitting"
          data-test="cancel-export"
          @click="close"
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="createDisabled"
          data-test="submit-export"
          @click="submit"
        >
          {{ $t('elabel_export_create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Utils from '@/utils/utils'
import brandsService from '@/services/brands'
import productsService from '@/services/products'
import { EXPORT_PRODUCT_LIMIT, namesDescription } from '@/utils/elabel-export-state'

const PRODUCT_SEARCH_DEBOUNCE_MS = 300
// The list is for picking a handful of products by name or SKU, so a longer list
// would only slow the menu down.
const PRODUCT_SEARCH_LIMIT = 50

function uniqueById (items) {
  const itemsById = new Map()
  items.forEach(item => {
    if (item && !itemsById.has(item.id)) itemsById.set(item.id, item)
  })
  return Array.from(itemsById.values())
}

function byName (left, right) {
  return String(left.name || '').localeCompare(String(right.name || ''))
}

export default {
  name: 'ElabelExportDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    campaigns: {
      type: Array,
      default: () => []
    },
    // The company has exactly one campaign, so the campaigns field is hidden and that
    // campaign applies to everything the dialog offers.
    singleCampaign: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      selectedCampaigns: [],
      selectedBrands: [],
      selectedProducts: [],
      // The full product records behind selectedProducts, kept so the chips still have
      // their names once a new search replaces the menu contents
      pickedProducts: [],
      brandOptions: [],
      productResults: [],
      productSearch: '',
      loadingBrands: false,
      loadingProducts: false,
      brandRequestCount: 0,
      productRequestCount: 0,
      debouncedProductSearch: null
    }
  },

  computed: {
    dialog: {
      get () {
        return this.value
      },
      set (isOpen) {
        this.$emit('input', isOpen)
      }
    },

    productLimit () {
      return EXPORT_PRODUCT_LIMIT
    },

    allCampaignsSelected () {
      return this.campaigns.length > 0 && this.selectedCampaigns.length === this.campaigns.length
    },

    allBrandsSelected () {
      return this.brandOptions.length > 0 && this.selectedBrands.length === this.brandOptions.length
    },

    // The campaigns the brand and product lists are narrowed to. With one campaign the
    // user never picks it, so it is filled in here.
    activeCampaignIds () {
      if (!this.singleCampaign) return this.selectedCampaigns
      const [onlyCampaign] = this.campaigns
      return onlyCampaign ? [onlyCampaign.id] : []
    },

    // A whole campaign, a whole brand (single-campaign companies) or a list of products
    // can be exported. The single campaign on its own is not a choice the user made, so
    // it does not count here.
    hasSelection () {
      if (this.singleCampaign) return Boolean(this.selectedBrands.length || this.selectedProducts.length)
      return this.selectedCampaigns.length > 0
    },

    productsLocked () {
      return this.singleCampaign ? !this.selectedBrands.length : !this.selectedCampaigns.length
    },

    lockedProductsPlaceholderKey () {
      return this.singleCampaign ? 'elabel_export_choose_brand_first' : 'elabel_export_choose_campaign_first'
    },

    // The API takes one category of ids per export. Every campaign (or every brand of the
    // only campaign) ticked means the whole company, which "all" says without listing ids.
    scope () {
      if (this.selectedProducts.length) {
        return { category: 'product', values: [...this.selectedProducts], description: namesDescription({ campaigns: this.coveredCampaignNames, brands: this.coveredBrandNames }) }
      }
      if (this.singleCampaign) {
        // "Select all" exports every product of the company, so the row says "All brands" rather than listing them
        if (this.allBrandsSelected) return { category: 'all', description: namesDescription({ campaigns: this.coveredCampaignNames, allBrands: true }) }
        return { category: 'brand', values: [...this.selectedBrands], description: namesDescription({ campaigns: this.coveredCampaignNames, brands: this.coveredBrandNames }) }
      }
      if (this.allCampaignsSelected) return { category: 'all', description: namesDescription({ allCampaigns: true }) }
      return { category: 'campaign', values: [...this.selectedCampaigns], description: namesDescription({ campaigns: this.coveredCampaignNames }) }
    },

    // The campaigns the export will cover, for the history's Campaigns column
    coveredCampaignNames () {
      let campaignIds = this.activeCampaignIds
      if (this.selectedProducts.length) {
        campaignIds = [...new Set(this.pickedProducts.map(product => product.campaign && product.campaign.id).filter(Boolean))]
      }
      return campaignIds
        .map(campaignId => (this.campaigns.find(campaign => campaign.id === campaignId) || {}).name)
        .filter(Boolean)
    },

    // Only single-campaign companies have the brand list loaded, so only their exports
    // record brands. Multi-campaign exports would need one brands request per campaign.
    coveredBrandNames () {
      if (!this.singleCampaign) return []
      let brandIds = this.selectedBrands
      if (this.selectedProducts.length) {
        brandIds = [...new Set(this.pickedProducts.map(product => product.brand && product.brand.id).filter(Boolean))]
      } else if (this.allBrandsSelected) {
        brandIds = this.brandOptions.map(brand => brand.id)
      }
      return brandIds
        .map(brandId => (this.brandOptions.find(brand => brand.id === brandId) || {}).name)
        .filter(Boolean)
    },

    createDisabled () {
      return !this.hasSelection
    },

    brandItems () {
      return this.brandOptions
    },

    productItems () {
      return uniqueById([...this.productResults, ...this.pickedProducts])
    },

    productsAtLimit () {
      return this.selectedProducts.length >= EXPORT_PRODUCT_LIMIT
    },

    // Counts what the export will cover. With products picked, the campaigns (or brands
    // for a single-campaign company) are those of the picked products.
    sentence () {
      const everythingTicked = !this.selectedProducts.length && (this.singleCampaign ? this.allBrandsSelected : this.allCampaignsSelected)
      if (everythingTicked) {
        return this.$t(this.singleCampaign ? 'elabel_export_sentence_all_brands' : 'elabel_export_sentence_all_campaigns')
      }
      let sourceCount
      if (this.selectedProducts.length) {
        const sourceOf = this.singleCampaign ? product => product.brand && product.brand.id : product => product.campaign && product.campaign.id
        sourceCount = new Set(this.pickedProducts.map(sourceOf).filter(Boolean)).size
      } else {
        sourceCount = this.singleCampaign ? this.selectedBrands.length : this.selectedCampaigns.length
      }
      const sources = this.$tc(this.singleCampaign ? 'elabel_export_brand_count' : 'elabel_export_campaign_count', sourceCount, { count: sourceCount })
      if (!this.selectedProducts.length) return this.$t('elabel_export_sentence_all_products', { sources })
      const products = this.$tc('elabel_export_product_count', this.selectedProducts.length, { count: this.selectedProducts.length })
      return this.$t('elabel_export_sentence', { products, sources })
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (isOpen) {
        if (!isOpen) return
        this.resetSelection()
        if (this.singleCampaign) this.loadBrandOptions()
        this.refreshProductList()
      }
    },

    selectedCampaigns () {
      // A campaign that is no longer picked takes its products with it
      this.dropProductsOutsideSelection()
      this.refreshProductList()
    },

    selectedBrands () {
      this.dropProductsOutsideSelection()
      this.refreshProductList()
    },

    selectedProducts (productIds) {
      this.pickedProducts = productIds
        .map(productId => this.productItems.find(product => product.id === productId))
        .filter(Boolean)
    },

    productSearch () {
      this.refreshProductList()
    }

  },

  created () {
    this.debouncedProductSearch = Utils.debounce(searchText => {
      this.searchProducts(searchText)
    }, PRODUCT_SEARCH_DEBOUNCE_MS)
  },

  methods: {
    close () {
      this.$emit('input', false)
    },

    submit () {
      this.$emit('submit', this.scope)
    },

    resetSelection () {
      this.selectedCampaigns = []
      this.selectedBrands = []
      this.selectedProducts = []
      this.pickedProducts = []
      this.productResults = []
      this.productSearch = ''
    },

    // Opening the dialog and changing a campaign or brand each ask for the list again; the
    // debounce folds the burst of watchers this sets off into one request
    refreshProductList () {
      this.loadingProducts = true
      this.debouncedProductSearch(this.productSearch)
    },

    productLabel (product) {
      if (!product) return ''
      return product.sku ? `${product.name} – ${product.sku}` : product.name || ''
    },

    toggleAllCampaigns () {
      this.selectedCampaigns = this.allCampaignsSelected ? [] : this.campaigns.map(campaign => campaign.id)
    },

    toggleAllBrands () {
      this.selectedBrands = this.allBrandsSelected ? [] : this.brandOptions.map(brand => brand.id)
    },

    // Past the limit the remaining products stop being selectable; picked ones can still be removed
    isProductDisabled (product) {
      return this.productsAtLimit && !this.selectedProducts.includes(product.id)
    },

    matchesSelection (product) {
      if (!product.elabel) return false
      if (this.activeCampaignIds.length && !(product.campaign && this.activeCampaignIds.includes(product.campaign.id))) return false
      if (this.selectedBrands.length && !(product.brand && this.selectedBrands.includes(product.brand.id))) return false
      return true
    },

    dropProductsOutsideSelection () {
      const keptProductIds = this.pickedProducts.filter(product => this.matchesSelection(product)).map(product => product.id)
      if (keptProductIds.length !== this.selectedProducts.length) this.selectedProducts = keptProductIds
    },

    // Only single-campaign companies show the Brands field today; the per-campaign fan-out
    // stays so the field can return for multi-campaign companies
    async loadBrandOptions () {
      this.brandRequestCount += 1
      const requestCount = this.brandRequestCount
      this.loadingBrands = true
      const campaignIds = this.activeCampaignIds
      try {
        const brandLists = await Promise.all(campaignIds.length
          ? campaignIds.map(campaignId => brandsService.loadBrands({ campaign: campaignId }))
          : [brandsService.loadBrands()])
        // A slower earlier request must not replace the newest list
        if (requestCount !== this.brandRequestCount) return
        this.brandOptions = uniqueById(brandLists.reduce((brands, list) => brands.concat(list || []), [])).sort(byName)
      } catch (error) {
        // The brands already listed stay, so a failed request does not wipe the picks
      } finally {
        if (requestCount === this.brandRequestCount) this.loadingBrands = false
      }
    },

    // Searched server-side because a company can have thousands of products. The endpoint
    // filters by one campaign and one brand, so one request goes out per pair.
    async searchProducts (searchText) {
      this.productRequestCount += 1
      const requestCount = this.productRequestCount
      if (this.productsLocked) {
        this.productResults = []
        this.loadingProducts = false
        return
      }
      const query = { limit: PRODUCT_SEARCH_LIMIT }
      const trimmedSearch = (searchText || '').trim()
      if (trimmedSearch) query.search = trimmedSearch
      const campaignIds = this.activeCampaignIds.length ? this.activeCampaignIds : [undefined]
      const brandIds = this.selectedBrands.length ? this.selectedBrands : [undefined]
      const queries = []
      campaignIds.forEach(campaignId => {
        brandIds.forEach(brandId => {
          queries.push({ ...query, campaign: campaignId, brand: brandId })
        })
      })
      try {
        const pages = await Promise.all(queries.map(productQuery => productsService.loadProducts(productQuery)))
        if (requestCount !== this.productRequestCount) return
        const foundProducts = pages.reduce((products, page) => products.concat((page && page.results) || []), [])
        this.productResults = uniqueById(foundProducts).filter(product => this.matchesSelection(product))
      } catch (error) {
        if (requestCount === this.productRequestCount) this.productResults = []
      } finally {
        if (requestCount === this.productRequestCount) this.loadingProducts = false
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.scope-field.v-select--chips:not(.v-input--is-focused):not(.v-input--is-dirty) ::v-deep input
  max-height: 32px
  padding: 8px 0

.field-label
  color: rgba(0, 0, 0, .6)
  font-size: 12px
  letter-spacing: .03em
  margin-bottom: 4px
</style>
